import React, { useState, useEffect } from "react";
import { FaUsers } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { CiSearch } from "react-icons/ci";
import "../styles/ManageTeams.css";
import axios from "axios";
import qs from "qs";
import {
  CircularProgressbar,
  buildStyles,
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";
import { SessionExtend } from "components/utils/SessionCheckUtils";
import Select, { components } from "react-select";
import { Filter } from "lucide-react";
import openNotification from "components/utils/CustomNotification";

interface Teams {
  org_uid: string;
  group_uid: string;
  group_name: string;
  group_type: string;
  description: string;
  code: string | null;
  status: string;
  reason: string;
  source: string;
  created_at: string;
  created_by: string;
  visibility?: string;
  updated_at: string | null;
  updated_by: string | null;
  users_count: number;
  active_users: number;
  summary_metrics?: SummaryMetrics;
}

interface GroupItem {
  group_name: string;
  group_uid: string;
  is_member: boolean;
}

interface GroupFilters {
  department: GroupItem[];
  location: GroupItem[];
  projectteam: GroupItem[];
  Custom?: GroupItem[];
  clientteam: GroupItem[];
}

interface MetricDetail {
  target: number;
  value: number;
  unit: string;
  performance: number;
  gcr: number;
  goal_count: number;
  achieved_goals: number;
}

interface DailyMetrics {
  type: string;
  avg_performance: number;
  avg_gcr: number;
  total_achieved_goals: number;
  total_goal_count: number;
  steps: MetricDetail;
  distance: MetricDetail;
  caloriesburned: MetricDetail;
}

interface SummaryMetrics {
  overall_performance: number;
  overall_gcr: number;
  overall_achieved_goals: number;
  overall_goal_count: number;
  metrics: DailyMetrics[];
}
interface Leader {
  user_uid: string;
  user_name: string;
  name: string;
  department: string;
  project: string;
  job_title: string;
  group_uid: string;
}


const ManageTeams: React.FC = () => {
  useCheckSessionHeartBeat();
  const navigate = useNavigate();
  const handleLeaderClick = (leaderUid: string) => {
    localStorage.setItem("leader_uid", leaderUid);
    navigate("/leader-hierarchy");
  };

  const sessionUid = localStorage.getItem("session_uid");
  const userUid = localStorage.getItem("user_uid");
  const role = localStorage.getItem("user_role");

  const [teams, setTeams] = useState<Teams[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<Teams[]>([]);
  const [groupFilters, setGroupFilters] = useState<GroupFilters>({
    department: [],
    location: [],
    projectteam: [],
    clientteam: [],
  });
  const [myTeamUids, setMyTeamUids] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedProjectTeams, setSelectedProjectTeams] = useState<string[]>([]);
  const [selectedCustomTeams, setSelectedCustomTeams] = useState<string[]>([]);
  const [selectedClientTeams, setSelectedClientTeams] = useState<string[]>([]);
  const [noTeams, setNoTeams] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("manageTeamsActiveTab") || "teams";
  });


  const [leaders, setLeaders] = useState<Leader[]>([]);
  const [expandedLeaderUids, setExpandedLeaderUids] = useState<string[]>([]);
  const [leaderTeams, setLeaderTeams] = useState<Teams[]>([]);
  const [isLeaderTeamsLoading, setIsLeaderTeamsLoading] = useState(false);
  const [leaderSelfMetrics, setLeaderSelfMetrics] = useState<any>(null);
  const [leaderTopPerformers, setLeaderTopPerformers] = useState<any[]>([]);
  const loggedInUserUid = localStorage.getItem("user_uid");
  const [leaderTeamMetrics, setLeaderTeamMetrics] = useState<Record<string, any>>({});
  const [myTeams, setMyTeams] = useState<Teams[]>([]);
  const [appliedDepartmentUids, setAppliedDepartmentUids] = useState([]);
  const [pendingDepartments, setPendingDepartments] = useState<string[]>([]);
  const [pendingProjectTeams, setPendingProjectTeams] = useState<string[]>([]);
  const [pendingLocations, setPendingLocations] = useState<string[]>([]);
  const [activeMenu, setActiveMenu] = useState<"department" | "project" | "location" | "client" | null>(null);
  const [isFetchingMetrics, setIsFetchingMetrics] = useState(false);
  const [compareError, setCompareError] = useState("");
  const [userImages, setUserImages] = useState<{ [key: string]: string }>({});
  const [pendingClientTeams, setPendingClientTeams] = useState<string[]>([]);

  const departmentOptions = groupFilters.department.map((item) => ({
    label: item.group_name,
    value: item.group_uid,
    isMine: myTeamUids.includes(item.group_uid),
  }));

  const locationOptions = groupFilters.location.map((item) => ({
    label: item.group_name,
    value: item.group_uid,
    isMine: myTeamUids.includes(item.group_uid),
  }));

  const projectTeamOptions = groupFilters.projectteam.map((item) => ({
    label: item.group_name,
    value: item.group_uid,
    isMine: myTeamUids.includes(item.group_uid),
  }));
  const clientTeamOptions =
    groupFilters?.clientteam?.map((item) => ({
      label: item.group_name,
      value: item.group_uid,
      isMine: item.is_member,
    })) || [];

  const customTeamOptions = groupFilters.Custom?.map((item) => ({
    label: item.group_name,
    value: item.group_uid,
    isMine: myTeamUids.includes(item.group_uid),
  })) || [];

  useEffect(() => {
    if (selectedTeams.length > 0) {
      setSelectedTeams([]);
    }
  }, [
    pendingDepartments,
    pendingLocations,
    pendingProjectTeams,
    pendingClientTeams,
  ]);

  const handleExpandLeader = async (userUid: string) => {
    // Check if this leader is already expanded
    if (expandedLeaderUids.includes(userUid)) {
      // Collapse this leader
      setExpandedLeaderUids(expandedLeaderUids.filter(id => id !== userUid));
      // Remove its metrics from the cache
      const updatedMetrics = { ...leaderTeamMetrics };
      delete updatedMetrics[userUid];
      setLeaderTeamMetrics(updatedMetrics);
      return;
    }

    // Add this leader to expanded list
    setExpandedLeaderUids([...expandedLeaderUids, userUid]);
    setIsLeaderTeamsLoading(true);

    try {
      const now = new Date();
      const toDate = now.toISOString();
      const fromDate = new Date(now.getTime() - 60 * 60 * 1000).toISOString();

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/get-hierarchy-metrics`,
        {
          headers: {
            "user-uid": loggedInUserUid!,
          },
          params: {
            target_user_uid: userUid,
            from_datetime: fromDate,
            to_datetime: toDate,
          }, withCredentials: true,
        }
      );

      if (response.data.status === "Success") {
        const data = response.data.data;
        // Store metrics for this specific leader
        setLeaderTeamMetrics(prev => ({
          ...prev,
          [userUid]: data.vertical_metrics || null
        }));
      } else {
        // Store empty metrics for this leader
        setLeaderTeamMetrics(prev => ({
          ...prev,
          [userUid]: null
        }));
      }
    } catch (error) {
      openNotification({
        type: "error",
        message: "Unable to fetch the leader's hierarchy metrics. Please try again later."
      });

      // Store empty metrics for this leader on error
      setLeaderTeamMetrics(prev => ({
        ...prev,
        [userUid]: null
      }));
    }

    finally {
      setIsLeaderTeamsLoading(false);
    }
  };

  const toggleExpand = (id: string | null) => {
    setExpandedTeamId(id);
  };

  const handleViewReportees = (leader: any) => {
    navigate("/view-reportees", {
      state: {
        target_user_uid: leader.user_uid,
        leader_name: leader.user_name,
      },
    });
  };



  const handleViewTeam = (
    group_uid: string,
    org_uid: string,
    users_count: number,
    active_users: number
  ) => {
    navigate("/teams-member-list", {
      state: {
        groupUid: group_uid,
        orgUid: org_uid,
        userCount: users_count,
        activeUsers: active_users,
      },
    });
  };

  const getInitialGroupUidsByTab = (
    tab: string,
    filterData: any
  ): string[] => {
    if (tab === "location") {
      return filterData.location?.map((loc: any) => loc.group_uid) || [];
    }

    return filterData.my_teams || [];
  };

  const fetchGroupFilters = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/get-groups-filter`, {
        headers: {
          "user-uid": userUid!,
        }, withCredentials: true,
      });

      if (response.data.status === "Success") {
        const filterData = response.data.data;
        setGroupFilters(filterData);
        setLeaders(filterData.Leaders);

        // const myTeamGroupUids: string[] = filterData.my_teams || [];

        // const locationGroupUids: string[] =
        //   filterData.location?.map((loc: any) => loc.group_uid) || [];

        const initialGroupUids = getInitialGroupUidsByTab(activeTab, filterData);

        setMyTeamUids(initialGroupUids);

        if (initialGroupUids.length > 0) {
          getOrgTeamsMetrics(initialGroupUids, false);
          // getOrgTeamsMetrics(locationGroupUids, false);
          setNoTeams(false);
          setTeams(filterData.my_teams);
        } else {
          setTeams([]);
          setNoTeams(true);
          // Don't show notification - user might be viewing Leaders tab
          // The UI will show "No teams to display" message when on Teams/Location tab
        }

        setLoading(false);
      } else {
        openNotification({
          type: "error",
          message: response.data.data,
        });
        setTeams([]);
        setNoTeams(true);
        setLoading(false);
      }

      SessionExtend();
    } catch (err) {
      openNotification({
        type: "error",
        message: "Could not fetch teams or group filters. Please try again.",
      });
      setTeams([]);
      setNoTeams(true);
      setLoading(false);
    }
  };

  const fetchUserImages = async (userUids: string[]) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/user-profile-images`, {
        headers: {
          "user-uid": localStorage.getItem("user_uid") || "",
        },
        params: {
          icon_img: true,
          target_user_uid_list: userUids,
        }, withCredentials: true,
        paramsSerializer: params =>
          qs.stringify(params, { arrayFormat: "repeat" }),
        responseType: "json",
      });

      const imageMap: Record<string, string> = {};
      const profileData = response.data?.data || [];

      profileData.forEach((entry: any) => {
        const blankGifBase64 = "R0lGODlhAQABAIABAAAAAP///yH5BAEAAAEALAAAAAABAAEAAAICRAEAOw==";

        if (entry.user_uid && entry.image) {
          const isBlankImage = entry.image === blankGifBase64;

          if (!isBlankImage) {
            imageMap[entry.user_uid] = `data:image/jpeg;base64,${entry.image}`;
          }
        }
      });

      setUserImages(prev => ({ ...prev, ...imageMap }));
    } catch (error) {
      console.warn("Failed to fetch user images", error);
    }
  };



  const getInitials = (name: string) => {
    const words = name.trim().split(/\s+/);
    const first = words[0]?.[0] || "";
    return (first).toUpperCase();
  };


  const getOrgTeamsMetrics = async (groupUidList: string[], isFromFilter = false) => {
    setLoading(true);
    setIsFetchingMetrics(true);

    const sessionUid = localStorage.getItem("session_uid");
    const userUid = localStorage.getItem("user_uid");

    if (!sessionUid || !userUid) {
      openNotification({
        type: "error",
        message: "Missing session. Please log in again.",
      });
      setLoading(false);
      setIsFetchingMetrics(false);
      return;
    }

    try {
      const now = new Date();
      const toDate = now.toISOString();
      const fromDate = new Date(now.getTime() - 60 * 60 * 1000).toISOString();

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/get-org-teams-metrics`,
        {
          headers: {
            "user-uid": userUid,
          },
          params: {
            from_datetime: fromDate,
            to_datetime: toDate,
            group_uid_list: groupUidList,
          },
          withCredentials: true,
          paramsSerializer: (params) => {
            const query = new URLSearchParams();
            query.append("from_datetime", params.from_datetime);
            query.append("to_datetime", params.to_datetime);

            if (params.group_uid_list.length === 0) {
              query.append("group_uid_list", "");
            } else {
              params.group_uid_list.forEach((uid: string) =>
                query.append("group_uid_list", uid)
              );
            }

            return query.toString();
          },
        }
      );

      if (response.data.status === "Success") {
        const all = response.data.data;
        setTeams(all);
        if (isFromFilter) {
          setNoTeams(all.length === 0);
        } else {
          setNoTeams(all.length === 0 && !myTeamUids.length);
        }
      }

      SessionExtend();
    } catch (error) {
      openNotification({
        type: "error",
        message: "Failed to fetch team metrics. Please try again.",
      });
    } finally {
      setIsFetchingMetrics(false);
      setLoading(false);
    }
  };

  const handleMyTeamsClick = async () => {
    setLoading(true);
    // Clear selected teams for comparison when switching to My Teams
    setSelectedTeams([]);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/get-groups-filter`, {
        headers: {
          "user-uid": userUid!,
        },
        withCredentials: true,
      });

      if (response.data.status === "Success") {
        const myTeamUids = (response.data.data.my_teams || []).map((uid: any) => uid);
        if (myTeamUids.length > 0) {
          getOrgTeamsMetrics(myTeamUids, false);
          setNoTeams(false);
        } else {
          setTeams([]);
          setNoTeams(true);
        }
      }
    } catch (err) {
      console.error("Error loading my teams", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (leaders.length > 0) {
      const userUids = leaders.map(user => user.user_uid);
      fetchUserImages(userUids);
    }
  }, [leaders]);

  useEffect(() => {
    const savedTab = localStorage.getItem("manageTeamsActiveTab");
    if (savedTab) {
      setActiveTab(savedTab);
    }
  }, []);

  // Clear selected teams if they're no longer in the current teams list
  useEffect(() => {
    if (selectedTeams.length > 0 && teams.length >= 0) {
      const currentTeamUids = teams.map(t => t.group_uid);
      const filteredSelectedTeams = selectedTeams.filter(st =>
        currentTeamUids.includes(st.group_uid)
      );

      // Only update if there's a difference
      if (filteredSelectedTeams.length !== selectedTeams.length) {
        setSelectedTeams(filteredSelectedTeams);
      }
    }
  }, [teams, selectedTeams]);


  const LeaderMetricsMini = ({ metrics }: { metrics: any }) => {
    const daily = metrics?.metrics?.[0] || {};

    return (
      <div className="leader-mini-metrics">
        {/* Reportees Performance */}
        <div className="leader-linear">
          <p className="mini-label">Reportees Performance</p>
          <div className="linear-track">
            <div
              className="linear-fill"
              style={{ width: `${Math.min(metrics.overall_performance ?? 0, 100)}%` }}
            ></div>
          </div>
          <span className="linear-value">{metrics.overall_performance?.toFixed(1) ?? 0}%</span>
        </div>

        {/* Reportees GCR */}
        <div className="leader-linear">
          <p className="mini-label">Reportees GCR</p>
          <div className="linear-track">
            <div
              className="linear-fill"
              style={{ width: `${Math.min(metrics.overall_gcr ?? 0, 100)}%` }}
            ></div>
          </div>
          <span className="linear-value">{metrics.overall_gcr?.toFixed(1) ?? 0}%</span>
        </div>

        {/* Steps / Calories / Distance */}
        <div className="middle-metrics">
          {["steps", "caloriesburned", "distance"].map((key) => {
            const label =
              key === "steps" ? "Steps" : key === "caloriesburned" ? "Calories" : "Distance";
            const metric = daily[key as keyof typeof daily] as any;
            const percent = metric?.target
              ? Math.round((metric.value / metric.target) * 100)
              : 0;

            return (
              <div className="semi-bar" key={key}>
                <div className="semi-bar-wrapper">
                  <div className="semi-bar-rotated">
                    <CircularProgressbar
                      value={Math.min(percent, 100)}
                      text={""}
                      styles={buildStyles({
                        pathColor: "#007c91",
                        trailColor: "#eee",
                        textColor: "transparent",
                      })}
                      circleRatio={0.5}
                      strokeWidth={10}
                    />
                  </div>
                  <div className="semi-bar-text">{percent}%</div>
                </div>
                <p className="mini-label">{label}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const PerformanceChart = ({ metrics }: { metrics: any }) => {
    if (!metrics || typeof metrics !== "object") {
      return <div>No performance data available</div>;
    }
    const metricData = metrics.metrics?.[0] || {};

    return (
      <>
        <div className="circular-metric">
          <CircularProgressbar
            value={Math.min(metrics.overall_performance ?? 0, 100)}
            text={`${metrics.overall_performance?.toFixed(0) ?? 0}%`}
            styles={buildStyles({
              pathColor: "#00a3ad",
              textColor: "#222",
              trailColor: "#eee",
              textSize: "16px",
            })}
          />
          <p className="mini-label">Overall Performance</p>
        </div>

        <div className="middle-metrics">
          {["steps", "caloriesburned", "distance"].map((key) => {
            const label =
              key === "steps" ? "Steps" : key === "caloriesburned" ? "Calories" : "Distance";
            const metric = metricData[key as keyof typeof metricData] as any;
            const percent =
              metric && metric.target ? Math.round((metric.value / metric.target) * 100) : 0;

            return (
              <div className="semi-bar" key={key}>
                <div className="semi-bar-wrapper">
                  <div className="semi-bar-rotated">
                    <CircularProgressbar
                      value={Math.min(percent, 100)}
                      text={""}
                      styles={buildStyles({
                        pathColor: "#007c91",
                        trailColor: "#eee",
                        textColor: "transparent",
                      })}
                      circleRatio={0.5}
                      strokeWidth={10}
                    />
                  </div>
                  <div className="semi-bar-text">{percent}%</div>
                </div>
                <p className="mini-label">{label}</p>
              </div>
            );
          })}
        </div>

        <div className="card-bottom">
          <div className="goal-block">
            <p>Overall Achieved Goals</p>
            <span className="goal-count">
              {metrics.overall_achieved_goals ?? 0}/{metrics.overall_goal_count ?? 0}
            </span>
            <div className="linear-track">
              <div
                className="linear-fill"
                style={{
                  width: `${metrics.overall_goal_count
                    ? Math.min(
                      (metrics.overall_achieved_goals / metrics.overall_goal_count) * 100,
                      100
                    )
                    : 0
                    }%`,
                }}
              ></div>
            </div>
          </div>

          <div className="goal-block">
            <p>Goal Completion Rate</p>
            <span className="goal-count">{metrics.overall_gcr ?? 0}%</span>
            <div className="linear-track">
              <div
                className="linear-fill"
                style={{
                  width: `${Math.min(metrics.overall_gcr ?? 0, 100)}%`,
                }}
              ></div>
            </div>
          </div>
        </div>
      </>
    );
  };
  useEffect(() => {
    fetchGroupFilters();
  }, []);

  // useEffect(() => {
  //   const selectedUids = [
  //     ...selectedDepartments,
  //     ...selectedLocations,
  //     ...selectedProjectTeams,
  //     ...selectedCustomTeams,
  //   ];

  //   if (selectedUids.length > 0) {
  //     getOrgTeamsMetrics(selectedUids, true);
  //   }
  // }, [selectedDepartments, selectedLocations, selectedProjectTeams, selectedCustomTeams]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const filteredTeams = teams.filter((team) =>
    (team.group_name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredLocationTeams = teams.filter((team) =>
    (team.group_name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isLeaderTeam = (group_uid: string) => {
    return leaders.some((leader) => leader.group_uid === group_uid);
  };

  return (
    <div className="manage-teams-container">

      <h2 style={{ textAlign: "left", color: "var(--text-color)" }}>View Teams</h2>
      <div className="manage-teams-tabs-wrapper">
        <div className="manage-teams-tabs full-width-tabs">
          <button
            className={`ManageTeams-tab-button ${activeTab === "location" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("location");
              localStorage.setItem("manageTeamsActiveTab", "location");
              const locationUids =
                groupFilters.location?.map((loc) => loc.group_uid) || [];

              getOrgTeamsMetrics(locationUids, false);
            }}
          >
            Location
          </button>

          <button
            className={`ManageTeams-tab-button ${activeTab === "teams" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("teams");
              localStorage.setItem("manageTeamsActiveTab", "teams");
              getOrgTeamsMetrics(myTeamUids, false);
            }}
          >
            Teams
          </button>

          {role !== 'guest' && (
            <button
              className={`ManageTeams-tab-button ${activeTab === "leaders" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("leaders");
                localStorage.setItem("manageTeamsActiveTab", "leaders");
              }}
            >
              Leaders
            </button>
          )}
        </div>

        {activeTab === "location" && (
          <div className="location-tab-wrapper">
            <button
              className="compare-trigger-btn small-compare-btn"
              onClick={() => {
                setShowCompareModal(true);
              }}
            >
              ⇆ Compare
            </button>
          </div>
        )}

      </div>


      {showCompareModal && (activeTab === "teams" || activeTab === "location") && (
        <div className="compare-modal-overlay">
          <div className="compare-modal">
            <h3>SELECT TEAMS TO COMPARE</h3>
            <p className="compare-instruction">Select minimum 2 and maximum 4 teams to compare.</p>
            <div className="team-list-scroll">
              {teams.map((team) => {
                const isSelected = selectedTeams.some((t) => t.group_uid === team.group_uid);
                return (
                  <div
                    key={team.group_uid}
                    className={`compare-team-row ${isSelected ? "selected" : ""}`}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedTeams(selectedTeams.filter((t) => t.group_uid !== team.group_uid));
                      } else if (selectedTeams.length < 4) {
                        setSelectedTeams([...selectedTeams, team]);
                      }
                    }}
                  >
                    <span>{team.group_name}</span>
                    <span>{team.created_by}</span>
                    <span>{team.users_count}</span>
                  </div>
                );
              })}
            </div>
            <div className="compare-modal-actions">
              <button
                className="compare-submit-btn"
                onClick={() => {
                  if (selectedTeams.length < 2) {
                    setCompareError("Select at least 2 teams to compare.");
                    return;
                  }
                  setCompareError("");
                  navigate("/teams-comparison", {
                    state: { selectedTeams },
                  });
                }}
              >
                ⇆ Compare
              </button>
              {compareError && (
                <p className="compare-error-text">{compareError}</p>
              )}
              <button
                className="compare-cancel-btn"
                onClick={() => {
                  setShowCompareModal(false);
                  setSelectedTeams([]);
                  setCompareError("");
                }}
              >

                ✖ Cancel
              </button>

            </div>
          </div>
        </div>
      )}


      {activeTab === "teams" && (
        <div className="teams-tab-wrapper">

          {/* 🔹 Departments Filter */}
          <div className="filter-box">
            <label className="filter-heading">Departments</label>
            <Select
              isMulti
              classNamePrefix="react-select"
              options={departmentOptions}
              value={departmentOptions.filter((opt) =>
                pendingDepartments.includes(opt.value)
              )}
              onChange={(selected) => {
                const values = selected.map((opt) => opt.value);
                setPendingDepartments(values);

                // Combine all currently active filters to ensure we don't hide teams 
                // that are still selected in Project or Client filters
                const allActiveUids = [
                  ...values,                // New Department values
                  ...pendingProjectTeams,   // Existing Project values
                  ...pendingClientTeams,    // Existing Client values
                  ...selectedCustomTeams    // Existing Custom values
                ];
                setTeams((prevTeams) =>
                  prevTeams.filter((team) => allActiveUids.includes(team.group_uid))
                );

                setSelectedTeams((prevSelected) =>
                  prevSelected.filter((team) => allActiveUids.includes(team.group_uid))
                );
              }}
              closeMenuOnSelect={false}
              placeholder="Select departments..."
              menuIsOpen={activeMenu === "department"}
              onMenuOpen={() => setActiveMenu("department")}
              onMenuClose={() => setActiveMenu(null)}
              menuPlacement="bottom"
              formatOptionLabel={(option: any) => (
                <div className="option-label-wrapper">
                  <span>{option.label}</span>
                  {option.isMine && <span className="option-your-team-tag">Your Team</span>}
                </div>
              )}
              styles={{
                menu: (base) => ({ ...base, zIndex: 1000, paddingTop: 0, maxHeight: "300px", overflowY: "hidden" }),
                control: (base) => ({ ...base, minHeight: "38px" }),
                menuList: (base) => ({ ...base, paddingTop: 0, maxHeight: "250px", overflowY: "auto" }),
              }}
              components={{
                Menu: (props) => (
                  <components.Menu {...props}>
                    <div className="apply-top-button-fixed">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Select all departments
                          const allDepartmentUids = departmentOptions.map(opt => opt.value);
                          setPendingDepartments(allDepartmentUids);
                        }}
                        className="apply-btn-inline"
                        style={{ width: 'auto', padding: '4px 10px' }}
                      >
                        Select All
                      </button>
                    </div>
                    {props.children}
                  </components.Menu>
                ),
              }}
            />
          </div>

          {/* 🔹 Project Team Filter */}
          <div className="filter-box">
            <label className="filter-heading">Project Team</label>
            <Select
              isMulti
              classNamePrefix="react-select"
              options={projectTeamOptions}
              value={projectTeamOptions.filter((opt) =>
                pendingProjectTeams.includes(opt.value)
              )}
              onChange={(selected) => {
                const values = selected.map((opt) => opt.value);
                setPendingProjectTeams(values);

                const allActiveUids = [
                  ...pendingDepartments,
                  ...values,
                  ...pendingClientTeams,
                  ...selectedCustomTeams
                ];
                setTeams((prevTeams) =>
                  prevTeams.filter((team) => allActiveUids.includes(team.group_uid))
                );
                setSelectedTeams((prevSelected) =>
                  prevSelected.filter((team) => allActiveUids.includes(team.group_uid))
                );
              }}
              closeMenuOnSelect={false}
              placeholder="Select project teams..."
              menuIsOpen={activeMenu === "project"}
              onMenuOpen={() => setActiveMenu("project")}
              onMenuClose={() => setActiveMenu(null)}
              menuPlacement="bottom"
              formatOptionLabel={(option: any) => (
                <div className="option-label-wrapper">
                  <span>{option.label}</span>
                  {option.isMine && <span className="option-your-team-tag">Your Team</span>}
                </div>
              )}
              styles={{
                menu: (base) => ({ ...base, zIndex: 1000, paddingTop: 0, maxHeight: "300px", overflowY: "hidden" }),
                control: (base) => ({ ...base, minHeight: "38px" }),
                menuList: (base) => ({ ...base, paddingTop: 0, maxHeight: "250px", overflowY: "auto" }),
              }}
              components={{
                Menu: (props) => (
                  <components.Menu {...props}>
                    <div className="apply-top-button-fixed">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Select all project teams
                          const allProjectTeamUids = projectTeamOptions.map(opt => opt.value);
                          setPendingProjectTeams(allProjectTeamUids);
                        }}
                        className="apply-btn-inline"
                        style={{ width: 'auto', padding: '4px 10px' }}
                      >
                        Select All
                      </button>
                    </div>
                    {props.children}
                  </components.Menu>
                ),
              }}
            />
          </div>

          {/* Client Team Filter */}
          <div className="filter-box">
            <label className="filter-heading">Client Teams</label>
            <Select
              isMulti
              classNamePrefix="react-select"
              options={clientTeamOptions}
              value={clientTeamOptions.filter((opt) =>
                pendingClientTeams.includes(opt.value)
              )}
              onChange={(selected) => {
                const values = selected.map((opt) => opt.value);
                setPendingClientTeams(values);

                const allActiveUids = [
                  ...pendingDepartments,
                  ...pendingProjectTeams,
                  ...values,
                  ...selectedCustomTeams
                ];

                setTeams((prevTeams) =>
                  prevTeams.filter((team) => allActiveUids.includes(team.group_uid))
                );
                setSelectedTeams((prevSelected) =>
                  prevSelected.filter((team) => allActiveUids.includes(team.group_uid))
                );
              }}
              closeMenuOnSelect={false}
              placeholder="Select client teams..."
              menuIsOpen={activeMenu === "client"}
              onMenuOpen={() => setActiveMenu("client")}
              onMenuClose={() => setActiveMenu(null)}
              menuPlacement="bottom"
              formatOptionLabel={(option: any) => (
                <div className="option-label-wrapper">
                  <span>{option.label}</span>
                  {option.isMine && <span className="option-your-team-tag">Your Team</span>}
                </div>
              )}
              styles={{
                menu: (base) => ({ ...base, zIndex: 1000, paddingTop: 0, maxHeight: "300px", overflowY: "hidden" }),
                control: (base) => ({ ...base, minHeight: "38px" }),
                menuList: (base) => ({ ...base, paddingTop: 0, maxHeight: "250px", overflowY: "auto" }),
              }}
              components={{
                Menu: (props) => (
                  <components.Menu {...props}>
                    <div className="apply-top-button-fixed">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Select all client teams
                          const allClientTeamUids = clientTeamOptions.map(opt => opt.value);
                          setPendingClientTeams(allClientTeamUids);
                        }}
                        className="apply-btn-inline"
                        style={{ width: 'auto', padding: '4px 10px' }}
                      >
                        Select All
                      </button>
                    </div>
                    {props.children}
                  </components.Menu>
                ),
              }}
            />
          </div>


          <div className="central-apply-wrapper">
            <button
              className="apply-btn-inline apply-filter-btn"
              onClick={() => {
                setSelectedDepartments(pendingDepartments);
                setSelectedProjectTeams(pendingProjectTeams);
                setSelectedClientTeams(pendingClientTeams);
                // Clear selected teams for comparison when filter is applied
                setSelectedTeams([]);
                const combinedUids = [
                  ...pendingDepartments,
                  ...pendingProjectTeams,
                  ...selectedCustomTeams,
                  ...pendingClientTeams,
                ];
                getOrgTeamsMetrics(combinedUids, true);
              }}
              disabled={
                pendingDepartments.length === 0 &&
                pendingProjectTeams.length === 0 &&
                pendingClientTeams.length === 0 &&
                selectedCustomTeams.length === 0
              }
            >
              <Filter size={14} style={{ marginRight: "6px" }} />
              Apply Filter
            </button>
          </div>

          {/* 🔹 My Teams Button */}
          <div className="quick-actions-row">
            <button className="apply-btn-inline" onClick={handleMyTeamsClick}>
              My Teams
            </button>
            <button
              className="compare-trigger-btn"
              onClick={() => {
                setShowCompareModal(true);
              }}
            >
              Compare
            </button>
          </div>

        </div>
      )}


      {activeTab === "leaders" && (
        <div className="leaders-tab-wrapper">
          {leaders.map((leader) => {
            const isExpanded = expandedLeaderUids.includes(leader.user_uid);
            const metrics = leaderTeamMetrics[leader.user_uid];

            return (
              <div
                key={leader.user_uid}
                className={`leader-card ${isExpanded ? "expanded-leader-card" : ""}`}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest(".leader-action-btn")) return;
                  handleExpandLeader(leader.user_uid);
                }}
              >
                <div className="leader-profile">
                  <div className="leader-avatar">
                    {userImages[leader.user_uid] ? (
                      <img
                        src={userImages[leader.user_uid]}
                        alt="Profile"
                        className="leader-avatar-img"
                      />
                    ) : (
                      <div className="leader-avatar-fallback">
                        {getInitials(leader.user_name)}
                      </div>
                    )}
                  </div>

                  <div className="leader-info">
                    <h4 className="leader-name">{leader.user_name}</h4>
                    <p className="leader-designation">{leader.job_title}</p>
                  </div>
                </div>

                <div className="ManageTeams-ActionButtons">
                  <button
                    className="leader-action-btn"
                    onClick={() => handleLeaderClick(leader.user_uid)}
                  >
                    ⇵ View Hierarchy
                  </button>
                  <button
                    onClick={() => handleViewReportees(leader)}
                    className="leader-action-btn"
                    style={{ marginLeft: '8px' }}
                  >
                    View Reportees
                  </button>
                </div>


                {isExpanded && (
                  <div className="leader-metrics-wrapper">
                    {isLeaderTeamsLoading && !metrics ? (
                      <div className="leader-loading">Loading metrics...</div>
                    ) : (
                      metrics?.data?.[0] && (
                        <LeaderMetricsMini metrics={metrics.data[0]} />
                      )
                    )}
                  </div>
                )}

              </div>
            );
          })}
        </div>
      )}


      {(activeTab === "location" || activeTab === "teams") && (
        loading || isFetchingMetrics ? (
          <div className="loading-spinner">Loading...</div>
        ) : teams.length === 0 ? (
          <div className="no-teams-fallback">
            <p>🛈 No teams to display. Try applying filters once..</p>
          </div>
        ) : (

          <div className="teams-grid">
            {expandedTeamId ? (
              filteredTeams
                .filter((team) => team.group_uid === expandedTeamId)
                .map((team) => (
                  <div key={team.group_uid} className="team-card expanded focused-card">
                    {/* Expanded layout */}
                    <button onClick={() => toggleExpand(null)} className="back-btn">
                      ← Back to all teams
                    </button>
                    <h3>{team.group_name}</h3>
                    <p><FaUsers /> {team.users_count} Members</p>
                    <p>Performance: {team.summary_metrics?.overall_performance ?? "--"}%</p>
                    <p>GCR: {team.summary_metrics?.overall_gcr ?? "--"}%</p>
                    {/* Add more details like steps, distance, etc here */}
                  </div>
                ))
            ) : (
              filteredTeams.map((team) => (
                <div key={team.group_uid} className="custom-team-card">
                  {myTeamUids.includes(team.group_uid) && (
                    <div className="your-team-tag">Your Team</div>
                  )}

                  <div className="card-top">
                    <div className="top-left">
                      <h3>{team.group_name}</h3>
                      <p className="team-type-visibility">
                        {team.group_type} • {team.visibility}
                      </p>
                      <p className="team-member-count">{team.users_count} Members</p>
                    </div>
                    <div className="top-right">
                      <CircularProgressbar
                        value={Math.min(team.summary_metrics?.overall_performance ?? 0, 100)}
                        text={`${team.summary_metrics?.overall_performance ?? 0}%`}
                        styles={buildStyles({
                          pathColor: "#00a3ad",
                          textColor: "var(--text-color)",
                          trailColor: "#eee",
                          textSize: "16px",
                        })}
                      />
                      <p className="mini-label">Overall Performance</p>
                    </div>
                  </div>

                  <div className="middle-metrics">
                    {["steps", "caloriesburned", "distance"].map((key) => {
                      const label =
                        key === "steps" ? "Steps" : key === "caloriesburned" ? "Calories" : "Distance";
                      const metric = team.summary_metrics?.metrics?.[0]?.[key as keyof typeof team.summary_metrics.metrics[0]] as any;
                      const percent = metric && metric.target
                        ? Math.round((metric.value / metric.target) * 100)
                        : 0;

                      return (
                        <div className="semi-bar" key={key}>
                          <div className="semi-bar-wrapper">
                            <div className="semi-bar-rotated">
                              <CircularProgressbar
                                value={Math.min(percent, 100)}
                                text={""}
                                styles={buildStyles({
                                  pathColor: "#007c91",
                                  trailColor: "#eee",
                                  textColor: "transparent",
                                })}
                                circleRatio={0.5}
                                strokeWidth={10}
                              />
                            </div>
                            <div className="semi-bar-text">{percent}%</div>
                          </div>
                          <p className="mini-label">{label}</p>
                        </div>


                      );
                    })}
                  </div>

                  <div className="card-bottom">
                    <div className="goal-block">
                      <p>Overall Achieved Goals</p>
                      <span className="goal-count">
                        {team.summary_metrics?.overall_achieved_goals ?? 0}/
                        {team.summary_metrics?.overall_goal_count ?? 0}
                      </span>
                      <div className="linear-track">
                        <div className="linear-fill"
                          style={{
                            width: `${team.summary_metrics?.overall_goal_count
                              ? Math.min((team.summary_metrics?.overall_achieved_goals / team.summary_metrics?.overall_goal_count) * 100, 100)
                              : 0
                              }%`
                          }}
                        ></div>

                      </div>
                    </div>

                    <div className="goal-block">
                      <p>Goal Completion Rate</p>
                      <span className="goal-count">
                        {team.summary_metrics?.overall_gcr ?? 0}%
                      </span>
                      <div className="linear-track">
                        <div className="linear-fill"
                          style={{
                            width: `${Math.min(team.summary_metrics?.overall_gcr ?? 0, 100)}%`
                          }}
                        ></div>

                      </div>
                    </div>
                  </div>

                  <button
                    className="ManageTeams-view-team-btn"
                    onClick={() =>
                      handleViewTeam(
                        team.group_uid,
                        team.org_uid,
                        team.users_count,
                        team.active_users
                      )
                    }
                  >
                    View Team →
                  </button>
                </div>

              ))
            )}
          </div>
        )
      )}
    </div>
  );
};

export default ManageTeams;
